/**
 * Live-Activity-Log — Glass-Box live feedback per state.
 *
 * v2 (2026-05-15): Echte Timestamps aus DB-Daten, nicht aus React-Render-Time.
 * Jede State-Zeile bekommt eine deterministische UUID — klick = kopieren →
 * der User kann mir „State `<uuid>` hängt" sagen und ich finde den exakten Frame.
 *
 * Erkennt STUCK-Zustand: card.state ∈ {ocr_pending, ocr_running} aber pg-boss
 * hat keinen aktiven Job → Worker hat aufgegeben, manueller Restart nötig.
 */
import { useEffect, useState } from "react";
import type { Card, Timesheet, StuckDiagnosis } from "../../lib/api";
import { api } from "../../lib/api";
import { stepUuid } from "../../lib/stepUuid";

// Nur diese Hypothesen sind ein ECHTES Problem (🔴 Restart nötig). running_normal/
// running_long = Sidecar rechnet (kein Absturz). Single Source mit der Stuck-Diagnose.
const REAL_PROBLEM = new Set(["worker_crashed", "no_job", "sidecar_silent"]);

const TRANSIENT_STATES = new Set(["ocr_pending", "ocr_running", "validated"]);

const STATE_HINT: Record<string, string> = {
  uploaded: "Datei abgelegt, Triage abgeschlossen. Wartet auf manuellen OCR-Start (process_mode=manual)",
  ocr_pending: "Job in der Queue (pg-boss). Wartet auf Worker-Pickup",
  ocr_running: "Worker führt sibit-ocr-Sidecar aus (Qwen3-VL ~5-10 min CPU)",
  ocr_complete: "OCR-Resultat liegt vor (data.days, data.header)",
  pending_review: "Manuelle Disambiguation nötig (kind=unknown oder OCR-Fehler)",
  pending_conflict: "Duplicate für gleichen Employee+Month — Phase 7",
  validated: "User hat validiert. Worker-Job calculate in der Queue",
  calculated: "data.calculation populiert (worker/calc-abrechnung.ts)",
  exported: "RZL-XLSX generiert, state final",
  // Terminal-States (Pipeline beendet, keine weiteren Aktionen)
  archived: "Pipeline beendet — Karte verworfen (discard) oder vom Admin archiviert. PDF bleibt auf Disk (DSGVO-Audit).",
  superseded: "Pipeline beendet — Karte wurde durch eine andere überschrieben (overwrite). Audit via superseded_by + superseded_at.",
};

const TERMINAL_END_STATES = new Set(["archived", "superseded", "exported"]);

const STATE_COLOR: Record<string, string> = {
  uploaded: "#9ca3af",
  ocr_pending: "#f59e0b",
  ocr_running: "#3b82f6",
  ocr_complete: "#3b82f6",
  pending_review: "#f59e0b",
  pending_conflict: "#dc2626",
  validated: "#0ea5e9",
  calculated: "#10b981",
  exported: "#6b7280",
  archived: "#6b7280",
  superseded: "#6b7280",
};

interface CardRunHistory {
  card_id: string;
  queue: string;
  jobs: Array<{
    id: string;
    state: string;
    retry_count: number;
    created_on: string;
    started_on: string | null;
    completed_on: string | null;
    age_s: number;
    runtime_s: number | null;
    output: any;
  }>;
}

export function LiveActivityLog(props: {
  card: Card | null;
  timesheet: Timesheet | null;
  pollIntervalMs?: number;
  onPolled?: () => void;
}) {
  const { card, pollIntervalMs = 4000, onPolled } = props;
  const [, tick] = useState(0);
  const [runHistory, setRunHistory] = useState<CardRunHistory | null>(null);
  const [diag, setDiag] = useState<StuckDiagnosis | null>(null);

  // 1-second tick for live duration display (only when state is transient)
  useEffect(() => {
    if (!card || !TRANSIENT_STATES.has(card.state)) return;
    const t = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(t);
  }, [card?.state]);

  // Trigger parent reload at pollIntervalMs while transient
  useEffect(() => {
    if (!card || !TRANSIENT_STATES.has(card.state)) return;
    const t = setInterval(() => onPolled?.(), pollIntervalMs);
    return () => clearInterval(t);
  }, [card?.state, pollIntervalMs, onPolled]);

  // Fetch per-card pg-boss run history (so we can show the actual DB error
  // from a failed/retry-looping run inside the STUCK diagnose box).
  useEffect(() => {
    if (!card) { setRunHistory(null); return; }
    let alive = true;
    fetch(`api/queue/card/${card.id}?queue=card.ocr`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(j => { if (alive) setRunHistory(j); })
      .catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, [card?.id, card?.state]);

  // Stuck-Diagnose (Single Source, sidecar-first): nur sinnvoll während ocr_running.
  // Sie unterscheidet „läuft (lange)" vom echten Worker-Tod — der naive
  // !ocr_queue_job_id-Check tat das NICHT (Fehlalarm „Worker hat aufgegeben").
  useEffect(() => {
    if (!card || card.state !== "ocr_running") { setDiag(null); return; }
    let alive = true;
    const fetchOnce = () => {
      api.cards.stuckDiagnosis(card.id)
        .then(d => { if (alive) setDiag(d); })
        .catch(() => { /* non-fatal — Diagnose ist Bonus */ });
    };
    fetchOnce();
    const t = setInterval(fetchOnce, pollIntervalMs);
    return () => { alive = false; clearInterval(t); };
  }, [card?.id, card?.state, pollIntervalMs]);

  if (!card) return null;

  // Determine the canonical "started at" for the current state from real DB data:
  //   ocr_pending  → ocr_queued_at (pg-boss creation) || updated_at
  //   ocr_running  → ocr_started_at (pg-boss start)   || updated_at
  //   uploaded     → created_at
  //   anything else → updated_at (last state mutation)
  const stateStartedAt = pickStateStartTs(card);
  const isTransient = TRANSIENT_STATES.has(card.state);
  const ageMs = stateStartedAt ? Date.now() - new Date(stateStartedAt).getTime() : null;

  // STUCK-Erkennung (sidecar-first, via Stuck-Diagnose): ECHTES Problem nur wenn die
  // Diagnose worker_crashed/no_job/sidecar_silent meldet (Sidecar arbeitet NICHT an der
  // Karte). running_long = Sidecar rechnet lange (KEIN Absturz). ocr_pending = wartet in
  // der Queue. Der frühere `!ocr_queue_job_id`-Check war ein Fehlalarm (feuerte sofort bei
  // ocr_pending und bei pg-boss-Job-Expiry während laufender cell_dispatch-OCR).
  const realProblem = !!diag && REAL_PROBLEM.has(diag.hypothesis);
  const runningLong = !!diag && diag.hypothesis === "running_long";
  const isStuck = realProblem;
  const stuckAgeMs = isStuck && card.updated_at
    ? Date.now() - new Date(card.updated_at).getTime()
    : 0;
  const stuckLabel = diag?.hypothesis === "sidecar_silent" ? "Sidecar antwortet nicht"
                   : diag?.hypothesis === "no_job" ? "kein Job in der Queue"
                   : "Worker abgestürzt";

  // Farbton: rot=echtes Problem, gelb=läuft lange (Sidecar rechnet), neutral sonst.
  const borderColor = isStuck ? "#dc2626" : runningLong ? "#f59e0b" : "#d1d5db";
  const bgColor = isStuck ? "#fef2f2" : runningLong ? "#fffbeb" : "#fafafa";
  const headerBorder = isStuck ? "#fca5a5" : runningLong ? "#fde68a" : "#e5e7eb";
  const headerBg = isStuck ? "#fee2e2" : runningLong ? "#fef3c7" : "#f3f4f6";

  const stateUuid = stepUuid(card.id, "state", card.state);

  return (
    <div className="live-activity-log" style={{
      marginTop: 16,
      border: `1px solid ${borderColor}`,
      borderRadius: 6,
      background: bgColor,
    }}>
      <div style={{
        padding: "6px 12px",
        borderBottom: `1px solid ${headerBorder}`,
        background: headerBg,
        fontWeight: 600,
        fontSize: 12,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}>
        <span>State-Inspektor</span>
        {isTransient && !isStuck && !runningLong && (
          <span style={{
            display: "inline-block",
            width: 8, height: 8, borderRadius: 4,
            background: "#3b82f6",
            animation: "lal-pulse 1.2s infinite ease-in-out",
          }} />
        )}
        {runningLong && (
          <span style={{ color: "#b45309", fontWeight: 700, fontSize: 11 }}>
            ⏱ läuft lange — Sidecar rechnet (kein Absturz)
          </span>
        )}
        {isStuck && (
          <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 11 }}>
            🔴 STUCK — {stuckLabel}
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 11, fontWeight: 400, color: "#6b7280" }}>
          Polling alle {pollIntervalMs / 1000}s · Timestamps aus DB
        </span>
      </div>

      <style>{`
        @keyframes lal-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(0.6); }
        }
      `}</style>

      <div style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: 12,
            background: STATE_COLOR[card.state] ?? "#9ca3af",
            color: "white", fontWeight: 600, fontSize: 11,
          }}>{card.state}</span>

          <StateUuidChip uuid={stateUuid} />

          {stateStartedAt && (
            <span style={{ color: "#6b7280", fontFamily: "system-ui", fontSize: 12 }}>
              seit {new Date(stateStartedAt).toLocaleString("de-AT")}
              {ageMs != null && (
                <span style={{
                  marginLeft: 6,
                  color: isStuck ? "#dc2626" : (isTransient ? "#1d4ed8" : "#9ca3af"),
                  fontWeight: isTransient || isStuck ? 600 : 400,
                }}>
                  {isTransient ? "⏱ " : "+ "}{formatDur(ageMs)}
                </span>
              )}
            </span>
          )}
        </div>

        <div style={{ marginTop: 8, fontFamily: "system-ui", fontSize: 12, color: "#374151" }}>
          {STATE_HINT[card.state] ?? "(unbekannter State)"}
        </div>

        {/* Sidecar-Live-Status während OCR läuft — genaue Zahlen zum Beobachten + Steuern. */}
        {card.state === "ocr_running" && diag?.sidecar?.active && (
          <div style={{ marginTop: 8, fontFamily: "system-ui", fontSize: 12, color: "#374151" }}>
            Sidecar: <code>{diag.sidecar.active.op_label}</code> · {diag.sidecar.active.last_detail}
            {" · läuft "}<strong>{Math.round(diag.sidecar.active.elapsed_total_s)}s</strong>
            {" · Hard-Timeout in "}{Math.max(0, Math.round(diag.sidecar.active.hard_timeout_s - diag.sidecar.active.elapsed_total_s))}s
            {" · seit Heartbeat "}{Math.round(diag.sidecar.active.elapsed_since_heartbeat_s)}s
          </div>
        )}

        {runningLong && (
          <div style={{
            marginTop: 10, padding: "8px 10px",
            background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 4, color: "#92400e", fontFamily: "system-ui", fontSize: 12,
          }}>
            <strong>⏱ Läuft lange — kein Absturz.</strong> {diag?.advice}
          </div>
        )}

        {isStuck && (
          <div style={{
            marginTop: 10, padding: "8px 10px",
            background: "#fef2f2", border: "1px solid #fca5a5",
            borderRadius: 4, color: "#7f1d1d", fontFamily: "system-ui", fontSize: 12,
          }}>
            <strong>Diagnose:</strong> {diag?.advice ?? `card.state ist ${card.state}, aber Sidecar rechnet nicht an dieser Karte.`}{" "}
            <strong>Stale seit:</strong> {formatDur(stuckAgeMs)}.

            {/* Show the actual DB error from the last failed/retry-loop run */}
            <FailedRunDetail runHistory={runHistory} />

            <div style={{ marginTop: 8, fontSize: 11, color: "#991b1b" }}>
              Typische Ursachen (falls kein konkreter Fehler oben):
              <ul style={{ margin: "2px 0 0 18px", padding: 0 }}>
                <li>Worker visibility-timeout während Long-Running-OCR</li>
                <li>Worker-Crash zwischen OCR-Complete und State-UPDATE-Query</li>
                <li>Orphan-Reclaim-Race (state.updated_at jünger als 10min)</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Failed-Run-Detail (pg-boss output extraction) ────────────────────────

function FailedRunDetail({ runHistory }: { runHistory: CardRunHistory | null }) {
  if (!runHistory || runHistory.jobs.length === 0) return null;
  // Find most recent job with an error-shaped output
  const job = runHistory.jobs.find(j => j.output && typeof j.output === "object"
    && (j.output.code || j.output.message || j.output.stack));
  if (!job) return null;
  const o = job.output as Record<string, unknown>;

  return (
    <div style={{ marginTop: 10, padding: "8px 10px", background: "#fff5f5", border: "1px dashed #fca5a5", borderRadius: 4 }}>
      <strong>Letzter Fehler aus pg-boss</strong>
      <span style={{ marginLeft: 8, fontSize: 11, color: "#7f1d1d" }}>
        Run {job.id.slice(0, 8)} · state={job.state} · retry={job.retry_count}
        {job.completed_on && <> · {new Date(job.completed_on).toLocaleString("de-AT")}</>}
      </span>
      <table style={{ marginTop: 6, fontSize: 11, fontFamily: "monospace", borderCollapse: "collapse" }}>
        <tbody>
          {renderRow("code", o.code)}
          {renderRow("message", o.message)}
          {renderRow("constraint", o.constraint)}
          {renderRow("detail", o.detail)}
          {renderRow("table", o.table)}
          {renderRow("severity", o.severity)}
          {renderRow("location", parseStackTop(String(o.stack ?? "")))}
        </tbody>
      </table>
      {typeof o.stack === "string" && (
        <details style={{ marginTop: 6 }}>
          <summary style={{ cursor: "pointer", fontSize: 11 }}>Full stack</summary>
          <pre style={{ fontSize: 10, whiteSpace: "pre-wrap", color: "#7f1d1d", margin: "4px 0 0 0" }}>{o.stack}</pre>
        </details>
      )}
    </div>
  );
}

function renderRow(label: string, value: unknown) {
  if (value == null || value === "") return null;
  return (
    <tr>
      <td style={{ padding: "1px 8px 1px 0", verticalAlign: "top", color: "#991b1b", fontWeight: 600 }}>{label}</td>
      <td style={{ padding: "1px 0", color: "#7f1d1d" }}>{String(value)}</td>
    </tr>
  );
}

function parseStackTop(stack: string): string {
  const m = stack.match(/at\s+(\S+)\s+\((\/[^)]+)\)/);
  if (m) return `${m[1]} (${m[2]})`;
  const lines = stack.split("\n");
  return lines.find(l => l.includes("/app/")) ?? lines[1] ?? "";
}

// ─── State-UUID Chip ──────────────────────────────────────────────────────

function StateUuidChip({ uuid }: { uuid: string }) {
  const [copied, setCopied] = useState(false);
  function copy(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard?.writeText) void navigator.clipboard.writeText(uuid).catch(() => {});
      else {
        const ta = document.createElement("textarea");
        ta.value = uuid; document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
    } catch { /* */ }
    try { window.getSelection()?.removeAllRanges(); } catch { /* fine */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 900);
  }
  return (
    <code
      onClick={copy}
      title={`State-UUID: ${uuid}\nKlick zum Kopieren — adressiert (Karte × State).`}
      style={{
        cursor: "pointer", padding: "2px 8px", borderRadius: 10,
        background: copied ? "#10b981" : "#dbeafe",
        color: copied ? "white" : "#1d4ed8",
        fontSize: 11, fontWeight: 600,
        userSelect: "none",
        border: copied ? "1px solid #10b981" : "1px solid #93c5fd",
      }}
    >
      {copied ? "✓ kopiert" : `⧉ ${uuid.slice(0, 8)}`}
    </code>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function pickStateStartTs(card: Card): string | null {
  switch (card.state) {
    case "uploaded":     return card.created_at ?? null;
    case "ocr_pending":  return card.ocr_queued_at ?? card.updated_at ?? null;
    case "ocr_running":  return card.ocr_started_at ?? card.updated_at ?? null;
    default:             return card.updated_at ?? null;
  }
}

function formatDur(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  if (m < 60) return `${m}m ${rs.toString().padStart(2, "0")}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h ${rm}m`;
}
